import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  attendanceStatuses,
  type AttendanceHistoryView,
  type AttendanceStatus,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { Link } from 'react-router-dom';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { attendanceKeys } from './scheduling';
import { errorKeyOf, type ErrorKey } from './BillingForms';

/** A dated record of actual presence and absence, separate from the aggregate rate. */
export function AttendanceHistory({
  locale,
  base,
  fromDate,
  toDate,
  departmentId,
}: {
  locale: SupportedLocale;
  base: string;
  fromDate: string;
  toDate: string;
  departmentId: string;
}): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<AttendanceStatus | ''>('');
  const [history, setHistory] = useState<AttendanceHistoryView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorKey | null>(null);
  const generation = useRef(0);
  const filters = JSON.stringify({ base, fromDate, toDate, departmentId, search, status });
  useEffect(() => {
    generation.current += 1;
    setHistory(null);
    setError(null);
    setBusy(false);
    return () => {
      generation.current += 1;
    };
  }, [filters]);

  async function load(offset: number): Promise<void> {
    if (busy) return;
    const currentGeneration = generation.current;
    const parameters = new URLSearchParams({ from: fromDate, to: toDate, offset: String(offset) });
    if (departmentId !== '') parameters.set('departmentId', departmentId);
    if (search.trim() !== '') parameters.set('search', search.trim());
    if (status !== '') parameters.set('status', status);
    setBusy(true);
    setError(null);
    try {
      const result = await apiRequest<AttendanceHistoryView>(
        `${base}/attendance-history?${parameters.toString()}`,
      );
      if (generation.current === currentGeneration)
        setHistory((previous) => ({
          ...result,
          items: offset === 0 ? result.items : [...(previous?.items ?? []), ...result.items],
        }));
    } catch (cause) {
      if (generation.current === currentGeneration) setError(errorKeyOf(cause));
    } finally {
      if (generation.current === currentGeneration) setBusy(false);
    }
  }

  return (
    <Box
      component="section"
      sx={{ mt: 3 }}
      aria-label={translate(locale, 'calendar.history.title')}
    >
      <Typography variant="h6" component="h3" gutterBottom>
        {translate(locale, 'calendar.history.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'calendar.history.help')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label={translate(locale, 'calendar.summary.athlete')}
          value={search}
          placeholder={translate(locale, 'calendar.history.allAthletes')}
          sx={{ minWidth: 200 }}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select
          size="small"
          label={translate(locale, 'calendar.history.status')}
          value={status}
          sx={{ minWidth: 200 }}
          onChange={(event) => {
            const value = event.target.value;
            setStatus(attendanceStatuses.find((candidate) => candidate === value) ?? '');
          }}
        >
          <MenuItem value="">{translate(locale, 'calendar.history.allStatuses')}</MenuItem>
          {attendanceStatuses.map((option) => (
            <MenuItem key={option} value={option}>
              {translate(locale, attendanceKeys[option])}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => {
            void load(0);
          }}
        >
          {translate(locale, 'calendar.history.show')}
        </Button>
      </Stack>
      {error !== null && <Alert severity="error">{translate(locale, error)}</Alert>}
      {history?.items.length === 0 && (
        <Typography>{translate(locale, 'calendar.summary.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0 }}>
        {history?.items.map((row) => (
          <Paper component="li" variant="outlined" key={row.participantId} sx={{ padding: 1.5 }}>
            <Typography sx={{ fontWeight: 600 }}>
              {athleteDisplayName(row, locale)} · {translate(locale, attendanceKeys[row.status])}
            </Typography>
            <Typography variant="body2">
              {formatCalendarDate(row.date, locale)} ·{' '}
              {new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: row.timezone,
              }).format(new Date(row.startsAt))}{' '}
              · {row.eventTitle}
            </Typography>
            <Button
              component={Link}
              to={`/events/${row.eventId}`}
              size="small"
              sx={{ minHeight: 44 }}
            >
              {translate(locale, 'calendar.history.openEvent')}
            </Button>
          </Paper>
        ))}
      </Stack>
      {history?.nextOffset !== null && history?.nextOffset !== undefined && (
        <Button
          disabled={busy}
          onClick={() => {
            void load(history.nextOffset ?? 0);
          }}
        >
          {translate(locale, 'billing.accounts.loadMore')}
        </Button>
      )}
    </Box>
  );
}
